import * as React from 'react';
import * as Toolbar from '@radix-ui/react-toolbar';
import {
  Button,
  FormField,
  Popover,
  StackLayout,
  TextInput,
} from '@leagueplatform/genesis-core';
import { useIntl } from '@leagueplatform/locales';
import {
  isBlockedProtocol,
  isHrefStructurallyValid,
} from '@web-config-app/validation';
import { RichtextToolbarButton } from '../richtext-toolbar-button/richtext-toolbar-button.component';
import { InsertLinkIcon } from '../../../toolbar-icons';
import { ToolbarProps } from '../../../rich-text-editor.types';

export const LinkEditPopover = ({
  editor,
  readOnly,
  allowedUriProtocols,
}: ToolbarProps): JSX.Element => {
  const { formatMessage } = useIntl();

  const [linkUrl, setLinkUrl] = React.useState<string>('');
  const [popoverOpen, setPopoverOpen] = React.useState(false);
  const [validationError, setValidationError] = React.useState<string | null>(
    null,
  );

  const validateLink = React.useCallback(
    (value: string): string | null => {
      if (!value) return null; // Treat empty value as remove action, no error
      const raw = value.trim();

      // If no allowed protocols list is supplied, treat all protocols as acceptable (no validation)
      if (!allowedUriProtocols || allowedUriProtocols.length === 0) {
        return null;
      }

      if (isBlockedProtocol(raw, allowedUriProtocols)) {
        // Extract just the scheme (substring before first ':') so message is cleaner
        const protocol = raw.split(':')[0];
        return formatMessage({ id: 'LINK_BLOCKED_PROTOCOL' }, { protocol });
      }

      if (!isHrefStructurallyValid(raw, allowedUriProtocols)) {
        return formatMessage({ id: 'LINK_INVALID_URL' });
      }
      return null;
    },
    [allowedUriProtocols, formatMessage],
  );

  const updateSelectedValue = React.useCallback(() => {
    const hasLink = editor?.isActive('link') || false;
    setPopoverOpen(hasLink);

    if (hasLink) {
      const current = editor?.getAttributes('link').href || '';
      setLinkUrl(current);
      setValidationError(validateLink(current));
    } else {
      setLinkUrl('');
      setValidationError(null);
    }
  }, [editor, validateLink]);

  React.useEffect(() => {
    editor?.on('selectionUpdate', updateSelectedValue);
    return () => {
      editor?.off('selectionUpdate', updateSelectedValue);
    };
  }, [editor, updateSelectedValue]);

  // Link Change Handler - called on enter or Save button
  const handleLinkChange = () => {
    // Removing link if empty
    if (!linkUrl.trim()) {
      // Act as remove
      editor?.chain().focus().unsetLink().run();
      setPopoverOpen(false);
      setValidationError(null);
      return;
    }
    // Validate on save attempt
    const linkError = validateLink(linkUrl);
    if (linkError) {
      setValidationError(linkError);
      return;
    }
    // Valid link - apply
    setValidationError(null);
    editor
      ?.chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: linkUrl.trim() })
      .run();
    setPopoverOpen(false);
  };

  // Insert Link
  const insertLink = React.useCallback(() => {
    // Do not create a link mark yet; open the popover for user to confirm via Save/Enter
    setLinkUrl('https://');
    setPopoverOpen(true);
    setValidationError(null);
  }, []);

  // Remove Link (called by the "Remove" button)
  const removeLink = () => {
    editor?.chain().focus().unsetLink().run();
    setPopoverOpen(false);
    setValidationError(null);
  };

  // Toolbar Link Button Click
  const onToolbarLinkButtonClick = () => {
    if (editor?.isActive('link')) {
      setPopoverOpen(true); // Open popover if a link is selected
    } else {
      insertLink(); // Insert a new link if no link exists
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleLinkChange();
    } else if (e.key === 'Escape') {
      setPopoverOpen(false);
    }
  };

  // (No protocol validation flag removed as not currently surfaced in UI)

  return (
    <Popover.Root open={popoverOpen}>
      <Popover.Trigger>
        <RichtextToolbarButton
          as={Toolbar.ToggleItem}
          icon={<InsertLinkIcon />}
          label={formatMessage({ id: 'INSERT_LINK_LABEL' })}
          active={editor?.isActive('link')}
          onClick={onToolbarLinkButtonClick}
          disabled={readOnly}
        />
      </Popover.Trigger>
      <Popover.Content
        align="center"
        side="bottom"
        showCloseButton={false}
        css={{ width: '20rem' }}
      >
        <StackLayout horizontalAlignment="center">
          <FormField
            id="link"
            label="Insert link"
            hideLabel
            inputStatus={validationError ? 'error' : undefined}
            statusMessage={validationError || undefined}
          >
            <TextInput
              value={linkUrl}
              onChange={(event) => {
                const val = event.target.value;
                setLinkUrl(val);
                // Live re-validate only if we already showed an error before
                if (validationError !== null) {
                  setValidationError(validateLink(val));
                }
              }}
              onKeyDown={onKeyDown}
              aria-invalid={!!validationError}
            />
          </FormField>
          <StackLayout
            orientation="horizontal"
            css={{ marginInlineStart: 'auto', marginBlockStart: '$quarter' }}
          >
            <Button
              size="toolbar"
              priority="secondary"
              quiet
              destructive
              onClick={removeLink}
            >
              {formatMessage({ id: 'REMOVE' })}
            </Button>
            <Button
              size="toolbar"
              priority="secondary"
              quiet
              onClick={handleLinkChange}
              disabled={!!validationError}
              css={{ marginBlockStart: '$quarter' }}
            >
              {formatMessage({ id: 'SAVE' })}
            </Button>
          </StackLayout>
        </StackLayout>
      </Popover.Content>
    </Popover.Root>
  );
};
